// src/components/common/SearchProductModalContent.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@/icons';
import ScrollableTable from './ScrollableTable';
import SearchButton from './SearchButton';
import SaveButton from './SaveButton';
import DateRangeInput from './DateRangeInput'; // Importação do novo componente
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { apiGet, apiPost } from '../../services/api';
import axios from 'axios';
import './SearchProductModalContent.css';
import { formatDateTime } from '../../utils/formatUtils';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';
import { OrigemRequisicao } from '../../constants';

interface Product {
  id: number; // Usado como requisicaoItemId
  requisicaoId: number;
  data: string; // Ajustado para um campo de data genérico
  codProduto: string;
  nomeProduto: string;
  codCategoria: string;
  nomeCategoria: string;
  qtdSaldoRequisicao: number;
  prioridade: string;
  nomeFantasiaLoja: string;
  nomeSolicitante: string;
  origem: OrigemRequisicao | null; // Origem da requisição (PLANEJAMENTO/BALCAO)
}

interface SearchProductModalContentProps {
  preOrdemId: number;
  theme: 'dark' | 'light';
  onClose: () => void;
  tipo: string[];
  status: string[];
  onItemsAdded: () => void; // Adicionado para recarregar a tabela pai
}

const SearchProductModalContent: React.FC<SearchProductModalContentProps> = ({
  preOrdemId,
  theme,
  onClose,
  tipo,
  status,
  onItemsAdded,
}) => {
  const [requisicao, setRequisicao] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [codProduto, setCodProduto] = useState('');
  const [nomeProduto, setNomeProduto] = useState('');
  const [codCategoria, setCodCategoria] = useState('');
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [loja, setLoja] = useState('');
  const [solicitante, setSolicitante] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [filterMode, setFilterMode] = useState<'Tudo' | 'Específico'>('Específico');
  const [quantities, setQuantities] = useState<{ [id: number]: number }>({});
  const shouldFetchRef = useRef(false);

  // Ref para capturar o modo atual de forma síncrona
  const currentModeRef = useRef(filterMode);

  // Estado para capturar os valores dos filtros de forma síncrona
  const filterValuesRef = useRef({
    requisicao: '',
    dataInicio: '',
    dataFim: '',
    codProduto: '',
    nomeProduto: '',
    codCategoria: '',
    nomeCategoria: '',
    prioridade: '',
    loja: '',
    solicitante: '',
  });

  // Atualiza filterValuesRef sempre que os estados mudam
  useEffect(() => {
    filterValuesRef.current = {
      requisicao,
      dataInicio,
      dataFim,
      codProduto,
      nomeProduto,
      codCategoria,
      nomeCategoria,
      prioridade,
      loja,
      solicitante,
    };
    logger.log('filterValuesRef atualizado:', filterValuesRef.current); // Log para depurar valores dos filtros
  }, [requisicao, dataInicio, dataFim, codProduto, nomeProduto, codCategoria, nomeCategoria, prioridade, loja, solicitante]);

  // Atualiza currentModeRef sempre que filterMode muda
  useEffect(() => {
    currentModeRef.current = filterMode;
    logger.log('currentModeRef atualizado para:', currentModeRef.current); // Log para depurar mudança de modo
  }, [filterMode]);

  const fetchTudo = useCallback(
    async (pageNumber: number, _currentItems: Product[]): Promise<{ items: Product[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });

      if (!tipo || tipo.length === 0) {
        await showAlert('O parâmetro tipoRequisicao é obrigatório.', { variant: 'error' });
        return { items: [], totalItems: 0 };
      }

      logger.log('fetchTudo chamado com tipo:', tipo, 'status:', status, 'page:', pageNumber);

      tipo.forEach((tipoItem) => queryParams.append('tipoRequisicao', tipoItem));
      status.forEach((statusItem) => queryParams.append('status', statusItem));

      const endpoint = `/v1/requisicoes/itens?${queryParams.toString()}`;

      try {
        const res = await apiGet(endpoint);
        logger.log('Resposta da API (Tudo):', res.data);
        logger.log('[SearchProductModalContent] Resposta bruta da API (fetchTudo):', JSON.stringify(res.data, null, 2));
logger.log('[SearchProductModalContent] totalElements (errado):', res.data.content[0]?.itens.totalElements);
logger.log('[SearchProductModalContent] totalElements (correto):', res.data.content[0]?.itens.page.totalElements);
        const items = res.data.content[0]?.itens.content.map((item: Record<string, unknown>) => ({
          id: item.id,
          requisicaoId: item.requisicaoId,
          data: item.dataCriacao || '',
          codProduto: item.codProduto,
          nomeProduto: item.nomeProduto,
          codCategoria: item.codCategoriaProduto?.toString() || '',
          nomeCategoria: item.nomeCategoria || '',
          qtdSaldoRequisicao: item.qtdSaldoRequisicao,
          prioridade: item.prioridade || 'Não definida',
          nomeFantasiaLoja: item.nomeFantasia || '',
          nomeSolicitante: item.nomeUsuario || '',
          origem: item.origem ?? null,
        })) || [];
        return { items, totalItems: res.data.content[0]?.itens.page.totalElements || 0 }; // Alterado para acessar o caminho correto da resposta da API, resolvendo undefined e permitindo paginação
      } catch (err) {
        logger.error('Erro ao buscar produtos (Tudo):', err);
        let errorMessage = 'Erro ao buscar produtos. Tente novamente.';
        if (axios.isAxiosError(err)) {
          errorMessage = err.response?.data?.error || errorMessage;
        }
        await showAlert(errorMessage, { variant: 'error' });
        return { items: [], totalItems: 0 };
      }
    },
    [tipo, status]
  );

  const fetchEspecifico = useCallback(
    async (pageNumber: number, _currentItems: Product[]): Promise<{ items: Product[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });

      // Usa os valores síncronos de filterValuesRef
      const {
        requisicao,
        dataInicio,
        dataFim,
        codProduto,
        nomeProduto,
        codCategoria,
        nomeCategoria,
        prioridade,
        loja,
        solicitante,
      } = filterValuesRef.current;

      logger.log('fetchEspecifico chamado com filtros (síncronos):', {
        requisicao,
        dataInicio,
        dataFim,
        codProduto,
        nomeProduto,
        codCategoria,
        nomeCategoria,
        prioridade,
        loja,
        solicitante,
        page: pageNumber,
      });

      // Validação local usando valores síncronos, mas sem considerar tipoRequisicao (enviado automaticamente)
      const hasFilter =
        (requisicao && requisicao.trim() !== '') ||
        (dataInicio && dataInicio.trim() !== '') ||
        (dataFim && dataFim.trim() !== '') ||
        (codProduto && codProduto.trim() !== '') ||
        (nomeProduto && nomeProduto.trim() !== '') ||
        (codCategoria && codCategoria.trim() !== '') ||
        (nomeCategoria && nomeCategoria.trim() !== '') ||
        (prioridade && prioridade.trim() !== '') ||
        (loja && loja.trim() !== '') ||
        (solicitante && solicitante.trim() !== '');

      logger.log('hasFilter (fetchEspecifico):', hasFilter); // Log para depurar a validação

      if (!hasFilter) {
        await showAlert('Pelo menos um parâmetro de filtro deve ser fornecido.', { variant: 'warning' });
        return { items: [], totalItems: 0 };
      }

      // Adiciona tipoRequisicao como parâmetro obrigatório, de forma oculta para o usuário
      if (!tipo || tipo.length === 0) {
        await showAlert('O parâmetro tipoRequisicao é obrigatório.', { variant: 'error' });
        return { items: [], totalItems: 0 };
      }
      tipo.forEach((tipoItem) => queryParams.append('tipoRequisicao', tipoItem));

      if (requisicao && requisicao.trim() !== '') queryParams.append('requisicaoId', requisicao.trim());
      if (dataInicio && dataInicio.trim() !== '') queryParams.append('dataInicio', dataInicio.trim());
      if (dataFim && dataFim.trim() !== '') queryParams.append('dataFim', dataFim.trim());
      if (codProduto && codProduto.trim() !== '') queryParams.append('codigo', codProduto.trim());
      if (nomeProduto && nomeProduto.trim() !== '') queryParams.append('nomeProduto', nomeProduto.trim());
      if (codCategoria && codCategoria.trim() !== '') queryParams.append('codigoCategoria', codCategoria.trim());
      if (nomeCategoria && nomeCategoria.trim() !== '') queryParams.append('categoria', nomeCategoria.trim());
      if (prioridade && prioridade.trim() !== '') queryParams.append('prioridade', prioridade.trim());
      if (loja && loja.trim() !== '') queryParams.append('loja', loja.trim());
      if (solicitante && solicitante.trim() !== '') queryParams.append('solicitante', solicitante.trim());

      const endpoint = `/v1/busca-inteligente/itens-pendente-compra-requisicao?${queryParams.toString()}`;

      try {
        const res = await apiGet(endpoint);
        logger.log('Resposta da API (Específico):', res.data);
        logger.log('[SearchProductModalContent] Resposta bruta da API (fetchEspecifico):', JSON.stringify(res.data, null, 2));
logger.log('[SearchProductModalContent] totalElements (errado):', res.data.content[0]?.itens.totalElements);
logger.log('[SearchProductModalContent] totalElements (correto):', res.data.content[0]?.itens.page.totalElements);
logger.log('[SearchProductModalContent] totalItems retornado (fetchEspecifico):', res.data.content[0]?.itens.totalElements || 0);
        const items = res.data.content[0]?.itens.content.map((item: Record<string, unknown>) => ({
          id: item.id,
          requisicaoId: item.requisicaoId,
          data: item.dataCriacao || '',
          codProduto: item.codProduto,
          nomeProduto: item.nomeProduto,
          codCategoria: item.codCategoriaProduto?.toString() || '',
          nomeCategoria: item.nomeCategoria || '',
          qtdSaldoRequisicao: item.qtdSaldoRequisicao,
          prioridade: item.prioridade || 'Não definida',
          nomeFantasiaLoja: item.nomeFantasia || '',
          nomeSolicitante: item.nomeUsuario || '',
          origem: item.origem ?? null,
        })) || [];
        return { items, totalItems: res.data.content[0]?.itens.page.totalElements || 0 }; // Alterado para acessar o caminho correto da resposta da API, resolvendo undefined e permitindo paginação
      } catch (err) {
        logger.error('Erro ao buscar produtos (Específico):', err);
        let errorMessage = 'Erro ao buscar produtos. Tente novamente.';
        if (axios.isAxiosError(err)) {
          errorMessage = err.response?.data?.error || errorMessage;
        }
        await showAlert(errorMessage, { variant: 'error' });
        return { items: [], totalItems: 0 };
      }
    },
    [tipo] // Adiciona tipo como dependência, já que agora é usado no fetchEspecifico
  );

  const fetchProducts = useCallback(
    async (pageNumber: number, currentItems: Product[]): Promise<{ items: Product[]; totalItems: number }> => {
      if (!shouldFetchRef.current) {
        logger.log('fetchProducts não chamado - shouldFetch:', shouldFetchRef.current);
        return { items: [], totalItems: 0 };
      }

      // Usa currentModeRef para garantir o modo correto
      const mode = currentModeRef.current;
      logger.log('fetchProducts chamado com modo (síncrono):', mode); // Log para depurar o modo atual

      if (mode === 'Tudo') {
        return fetchTudo(pageNumber, currentItems);
      } else {
        return fetchEspecifico(pageNumber, currentItems);
      }
    },
    [fetchTudo, fetchEspecifico] // Remove filterMode das dependências; usamos currentModeRef
  );

  const { items, hasMore, isLoading: fetchLoading, loadMore, reset } = usePaginatedFetch<Product>({
    fetchFunction: fetchProducts,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  const handleToggleFilterMode = () => {
    const newMode = filterMode === 'Tudo' ? 'Específico' : 'Tudo';
    logger.log('Alterando modo para:', newMode); // Log para depurar mudança de modo
    setFilterMode(newMode);
    
    // Limpa os inputs ao mudar o modo
    setRequisicao('');
    setDataInicio('');
    setDataFim('');
    setCodProduto('');
    setNomeProduto('');
    setCodCategoria('');
    setNomeCategoria('');
    setPrioridade('');
    setLoja('');
    setSolicitante('');
    
    // Limpa as quantidades, produtos selecionados e a tabela
    setQuantities({}); // Reseta as quantidades editadas
    setSelectedProducts([]); // Limpa os produtos selecionados
    shouldFetchRef.current = false; // Impede busca ao mudar o modo
    reset(); // Limpa a tabela (não dispara busca porque shouldFetchRef.current é falso)
  };

  const handleSearch = async () => {
    logger.log('handleSearch chamado com modo:', filterMode); // depura chamada do handleSearch
    logger.log('Estado dos filtros:', { // depura os valores dos estados
      requisicao,
      dataInicio,
      dataFim,
      codProduto,
      nomeProduto,
      codCategoria,
      nomeCategoria,
      prioridade,
      loja,
      solicitante,
    });

    if (filterMode === 'Específico') {
      const hasFilter =
        (requisicao && requisicao.trim() !== '') ||
        (dataInicio && dataInicio.trim() !== '') ||
        (dataFim && dataFim.trim() !== '') ||
        (codProduto && codProduto.trim() !== '') ||
        (nomeProduto && nomeProduto.trim() !== '') ||
        (codCategoria && codCategoria.trim() !== '') ||
        (nomeCategoria && nomeCategoria.trim() !== '') ||
        (prioridade && prioridade.trim() !== '') ||
        (loja && loja.trim() !== '') ||
        (solicitante && solicitante.trim() !== '');

      logger.log('hasFilter (handleSearch):', hasFilter); // depura a validação

      if (!hasFilter) {
        await showAlert('Pelo menos um parâmetro de filtro deve ser fornecido.', { variant: 'warning' });
        return;
      }
    }

    setIsLoading(true);
    shouldFetchRef.current = true; // Ativa a busca
    logger.log('Definindo shouldFetchRef.current como true');
    reset(); // Reseta e dispara a busca
    setQuantities({}); // Reseta as quantidades ao buscar novos itens
  };

  const handleSelectChange = (selectedItems: Product[]) => {
    setSelectedProducts(selectedItems);
    logger.log('Produtos selecionados:', selectedItems);
  };

  const handleQuantityChange = (id: number, value: string) => {
    const numValue = parseInt(value) || 1;
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(numValue, 1), // Garante que o valor seja maior que zero
    }));
  };

  const handleSave = async () => {
    if (selectedProducts.length === 0) {
      await showAlert('Selecione pelo menos um item para adicionar.', { variant: 'warning' });
      return;
    }

    const payload = {
      itens: selectedProducts.map((item) => ({
        requisicaoItemId: item.id,
        qtdProduto: quantities[item.id] || item.qtdSaldoRequisicao, // Usa a quantidade editada ou qtdSaldoRequisicao como fallback
      })),
    };

    try {
      setIsLoading(true);
      await apiPost(`/v1/pre-ordens/${preOrdemId}/itens`, payload);
      Toast.success('Item(s) Adicionados com Sucesso');
      onItemsAdded(); // Recarrega a tabela pai
      onClose(); // Fecha o modal
    } catch (err) {
      logger.error('Erro ao adicionar itens:', err);
      let errorMessage = 'Erro ao adicionar itens. Tente novamente.';
      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.error || errorMessage;
      }
      await showAlert(errorMessage, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Sincroniza isLoading com fetchLoading
  useEffect(() => {
    setIsLoading(fetchLoading);
  }, [fetchLoading]);

  // Impede busca inicial ao montar o componente
  useEffect(() => {
    reset(); // Limpa a tabela, mas não dispara busca devido a shouldFetchRef.current ser falso
  }, []); // Executa apenas uma vez ao montar

  const columns = [
    {
      header: 'Código',
      render: (item: Product) => item.codProduto,
    },
    {
      header: 'Cód. Categoria',
      render: (item: Product) => item.codCategoria,
    },
    {
      header: 'Nome do Produto',
      render: (item: Product) => item.nomeProduto,
    },
    {
      header: 'Categoria',
      render: (item: Product) => item.nomeCategoria,
    },
    {
      header: 'Quantidade',
      render: (item: Product) => (
        <input
          type="number"
          min="1"
          value={quantities[item.id] ?? item.qtdSaldoRequisicao}
          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
          className="quantity-input"
        />
      ),
    },
    {
      header: 'Data Solicitação',
      render: (item: Product) => (item.data ? formatDateTime(item.data) : 'N/A'),
    },
    {
      header: 'Requisição',
      render: (item: Product) => item.requisicaoId,
    },
    {
      header: 'Origem',
      render: (item: Product) => {
        if (item.origem === 'PLANEJAMENTO') return 'Planejamento';
        if (item.origem === 'BALCAO') return 'Balcão';
        return 'Não Identificado';
      },
    },
    {
      header: 'Prioridade',
      render: (item: Product) => item.prioridade,
    },
    {
      header: 'Loja',
      render: (item: Product) => item.nomeFantasiaLoja,
    },
    {
      header: 'Solicitante',
      render: (item: Product) => item.nomeSolicitante,
    },
  ];

  return (
    <div className={`search-product-modal-content ${theme}`}>
      <div className="header-container">
        <h2>
          <FontAwesomeIcon icon={faMagnifyingGlass} /> Pesquisar Produtos de uma Requisição
        </h2>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={filterMode === 'Tudo'}
            onChange={handleToggleFilterMode}
          />
          <span className="slider">
            <span className="slider-text">{filterMode}</span>
          </span>
        </label>
      </div>
      <div className="search-form">
        {filterMode === 'Específico' && (
          <div className="form-inputs">
            <div className="form-row">
              <input
                type="text"
                value={requisicao}
                onChange={(e) => setRequisicao(e.target.value)}
                placeholder="Requisição"
                className="search-input"
              />
              <DateRangeInput
                dataInicio={dataInicio}
                setDataInicio={setDataInicio}
                dataFim={dataFim}
                setDataFim={setDataFim}
                theme={theme}
              />
              <input
                type="text"
                value={codProduto}
                onChange={(e) => setCodProduto(e.target.value)}
                placeholder="Código"
                className="search-input"
              />
              <input
                type="text"
                value={nomeProduto}
                onChange={(e) => setNomeProduto(e.target.value)}
                placeholder="Nome do Produto"
                className="search-input"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                value={codCategoria}
                onChange={(e) => setCodCategoria(e.target.value)}
                placeholder="Código Categoria"
                className="search-input"
              />
              <input
                type="text"
                value={nomeCategoria}
                onChange={(e) => setNomeCategoria(e.target.value)}
                placeholder="Categoria"
                className="search-input"
              />
              <input
                type="text"
                value={prioridade}
                onChange={(e) => setPrioridade(e.target.value)}
                placeholder="Prioridade"
                className="search-input"
              />
              <input
                type="text"
                value={loja}
                onChange={(e) => setLoja(e.target.value)}
                placeholder="Loja"
                className="search-input"
              />
              <input
                type="text"
                value={solicitante}
                onChange={(e) => setSolicitante(e.target.value)}
                placeholder="Solicitante"
                className="search-input"
              />
            </div>
          </div>
        )}
        <SearchButton isLoading={isLoading} onClick={handleSearch}>
          Procurar
        </SearchButton>
      </div>
      <ScrollableTable<Product>
        data={items}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoading={isLoading}
        columns={columns}
        onSelectChange={handleSelectChange}
        emptyMessage="Nenhum produto encontrado."
        showCheckboxes={true}
        singleSelect={false} // Permite seleção múltipla
        isModal={true} // Indica que a tabela está dentro de um Modal
      />
      <div className="form-actions">
        <SaveButton isLoading={isLoading} onClick={handleSave} disabled={isLoading} />
      </div>
    </div>
  );
};

export default SearchProductModalContent;